/**
 * Core logic hook for the Schedule Interview wizard.
 * Wraps the generic useStepValidation hook with scheduling-specific
 * mutations, mode configuration, edit-mode hydration, and save logic.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { RefObject } from 'react';
import type { TabItem } from '@kula-ai/components';
import { useShowToast } from '@/hooks/useShowToast';
import {
  useCreateInterview,
  useUpdateInterview,
  useGetInterview,
} from '@/api/useInterviews';
import {
  useCreateSelfSchedule,
  useUpdateSelfSchedule,
  useGetSelfSchedule,
} from '@/api/useSelfSchedule';
import { useStepValidation, type StepFormApi } from '@/hooks/useStepValidation';
import type {
  ScheduleMode,
  ApiReference,
  ScheduleInterviewPayload,
  ScheduleModeConfig,
} from './config/schedule.types';
import { MODE_CONFIGS } from './config/modes';
import { SCHEDULE_INTERVIEW_COPY } from './config/schedule.copy';
import {
  buildSchedulePayload,
  buildSchedulePayloadFormData,
  hydratePayloadFromApi,
} from './payload';
import { hydrateCommunicationFromApi } from './payload/hydrateFromApi';
import { useInterviewPlan } from './api/useInterviewPlan';
import { api, ApiError } from '@/api/request';
import { getErrorMessage } from '@/utils/errors';
import { useMailBox } from '@/hooks/use-mailbox';

interface PhoneValue {
  countryCode: string;
  dialCode: string;
  number: string;
}

interface UseScheduleInterviewOptions {
  mode: ScheduleMode;
  apiReference: ApiReference;
  applicationId?: number;
  candidateId?: number;
  jobId?: number;
  stageId?: number;
  stageActivityId?: number;
  interviewId?: number;
  scheduleId?: number;
  /**
   * Candidate's current phone-on-record (from applicationData.contact).
   * Used to detect whether the user edited the phone on the Details
   * step so we can fire `PUT /api/v1/contacts/:id` only when it
   * actually changed — a view of the existing number shouldn't
   * generate a contact update.
   */
  initialCandidatePhone?: PhoneValue;
  onClose?: () => void;
  onSuccess?: () => void;
}

function phoneValuesEqual(
  a: PhoneValue | null | undefined,
  b: PhoneValue | null | undefined
): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return (
    (a.dialCode ?? '').trim() === (b.dialCode ?? '').trim() &&
    (a.number ?? '').trim() === (b.number ?? '').trim()
  );
}

export function useScheduleInterview({
  mode,
  apiReference,
  applicationId,
  candidateId,
  jobId,
  stageId,
  stageActivityId,
  interviewId,
  scheduleId,
  initialCandidatePhone,
  onClose,
  onSuccess,
}: UseScheduleInterviewOptions) {
  const toast = useShowToast();
  const baseConfig: ScheduleModeConfig = MODE_CONFIGS[mode];
  const isEdit = apiReference === 'edit';
  // Edit is a flat experience — the user is revising an existing
  // interview, not walking through a wizard for the first time, so the
  // progression-enforcing stepper feels wrong. Flip stepper configs
  // (adhoc, self-schedule-adhoc) to tabs so users can jump directly
  // between Details / Slot selection email / Communication / Scorecard.
  const config: ScheduleModeConfig =
    isEdit && baseConfig.navigationStyle === 'stepper'
      ? { ...baseConfig, navigationStyle: 'tabs' }
      : baseConfig;
  const isSelfSchedule =
    mode === 'self-schedule' || mode === 'self-schedule-adhoc';

  // ---- Edit-mode GET ----
  // Self-schedule edits hydrate from `/ats_schedules/:id` (flat
  // self-schedule shape — `time_slots`, `schedule_type`, `interview_kind`,
  // `contact`, mail bundle). Manual/adhoc edits hydrate from
  // `/ats_interviews/:id`. The two responses overlap enough that the
  // shared hydrator (`hydrateDetailsFromApi`) consumes either.
  const useScheduleGet = isEdit && isSelfSchedule && !!scheduleId;
  const {
    data: interviewFromInterviewsEndpoint,
    isLoading: isLoadingInterview,
    isError: isInterviewLoadError,
    isFetched: isFetchedInterview,
  } = useGetInterview(interviewId, {
    enabled: isEdit && !useScheduleGet && !!interviewId,
  });
  const {
    data: interviewFromSchedulesEndpoint,
    isLoading: isLoadingSchedule,
    isError: isScheduleLoadError,
    isFetched: isFetchedSchedule,
  } = useGetSelfSchedule(scheduleId, {
    enabled: useScheduleGet,
  });

  const existingInterview = useScheduleGet
    ? interviewFromSchedulesEndpoint
    : interviewFromInterviewsEndpoint;
  const isLoadingExisting = useScheduleGet
    ? isLoadingSchedule
    : isLoadingInterview;
  const isLoadError = useScheduleGet
    ? isScheduleLoadError
    : isInterviewLoadError;
  const isFetchedExisting = useScheduleGet
    ? isFetchedSchedule
    : isFetchedInterview;

  // ---- Payload ref + edit hydration ----
  const payloadRef = useRef<ScheduleInterviewPayload>({
    details: {},
    communication: {},
    scorecard: {},
  });

  const lastHydratedIdRef = useRef<number | null>(null);
  const prefillGenerationRef = useRef(0);

  if (!isEdit) {
    lastHydratedIdRef.current = null;
  }

  const editDataReady =
    isEdit &&
    existingInterview !== undefined &&
    isFetchedExisting &&
    !isLoadingExisting;

  if (editDataReady && existingInterview.id !== lastHydratedIdRef.current) {
    lastHydratedIdRef.current = existingInterview.id;
    prefillGenerationRef.current += 1;
    payloadRef.current = hydratePayloadFromApi(existingInterview);
  }

  const prefillVersion = isEdit ? prefillGenerationRef.current : 0;

  useEffect(() => {
    if (!isLoadError || !isEdit) return;
    toast({
      title: 'Could not load interview',
      description: 'Try closing and opening the editor again.',
      status: 'error',
    });
  }, [isEdit, isLoadError, toast]);

  // ---- Interview plan defaults (non-adhoc create) ----
  const isAdhoc = mode === 'adhoc' || mode === 'self-schedule-adhoc';
  const planEnabled = !isAdhoc && !isEdit && !!stageId && stageId > 0;
  const { data: interviewPlanFromNew, isLoading: isLoadingPlan } =
    useInterviewPlan(
      {
        stageId: stageId ?? 0,
        stageActivityId: stageActivityId ?? undefined,
        contactId: candidateId ?? undefined,
      },
      { enabled: planEnabled }
    );

  // Create + manual (has plan): seed the communication slot in
  // payloadRef from the plan's comm fields so CommunicationStep's
  // reducer initialiser picks up the plan's templates / bodies /
  // toggles on mount. The adhoc branch intentionally leaves this
  // empty — the step then falls back to "first template" defaults
  // because there's no plan to pull from.
  // Edit is covered by the `hydratePayloadFromApi` block above.
  const planCommHydratedRef = useRef(false);
  if (!isEdit) planCommHydratedRef.current = false;
  if (
    !isEdit &&
    !isAdhoc &&
    interviewPlanFromNew &&
    !planCommHydratedRef.current
  ) {
    planCommHydratedRef.current = true;
    payloadRef.current.communication =
      hydrateCommunicationFromApi(interviewPlanFromNew);
  }

  /** Create: `ats_interviews/new`. Edit: merge GET interview so Details
   *  / Schedule schema can read the same `plan.*` fields it reads on
   *  create (name/kind/location/participants/calendar_*). The plan
   *  endpoint is disabled in edit mode so `interviewPlanFromNew` is
   *  undefined here — existingInterview is the source of truth. */
  const interviewPlan = useMemo((): Record<string, unknown> | undefined => {
    if (!isEdit || !existingInterview) {
      return interviewPlanFromNew;
    }
    const ex = existingInterview as Record<string, unknown>;
    const base = (interviewPlanFromNew ?? {}) as Record<string, unknown>;
    return { ...ex, ...base };
  }, [interviewPlanFromNew, isEdit, existingInterview]);

  // ---- Step refs ----
  const stepRefsMap = useRef<Record<string, RefObject<StepFormApi | null>>>({});

  const getStepRef = useCallback(
    (stepKey: string): RefObject<StepFormApi | null> => {
      if (!stepRefsMap.current[stepKey]) {
        stepRefsMap.current[stepKey] = { current: null };
      }
      return stepRefsMap.current[stepKey];
    },
    []
  );

  const stepRefs = useMemo(() => {
    const refs: Record<string, RefObject<StepFormApi | null>> = {};
    for (const step of config.steps) {
      refs[step.key] = getStepRef(step.key);
    }
    return refs;
  }, [config.steps, getStepRef]);

  // ---- Mutations ----
  const createInterview = useCreateInterview();
  const updateInterview = useUpdateInterview();
  const createSelfSchedule = useCreateSelfSchedule();
  const updateSelfSchedule = useUpdateSelfSchedule();

  // CORE-4040's `validate_comm_email_senders` rejects the save when
  // `send_email_to_*` is on and the sender user's `connected_mailbox?`
  // is false. `isMailBoxConnected` mirrors that server-side check on
  // the current user (Gmail OR MS Exchange scopes present). We pre-
  // flight so the user doesn't lose the email body they typed to a
  // round-trip that was always going to 422.
  const { isMailBoxConnected } = useMailBox();

  const isSaving =
    createInterview.isPending ||
    updateInterview.isPending ||
    createSelfSchedule.isPending ||
    updateSelfSchedule.isPending;

  // ---- Save (onComplete callback for useStepValidation) ----
  const handleComplete = useCallback(
    async (payload: ScheduleInterviewPayload) => {
      const contextIds = {
        ats_application_id: applicationId,
        ats_job_id: jobId,
        ats_job_stage_id: stageId,
        ats_job_stage_action_id: stageActivityId ?? null,
        candidate_id: candidateId,
      };

      // Pre-flight: if the user toggled email-to-candidate or
      // email-to-interviewer ON but hasn't connected a mailbox, the
      // CORE-4040 `validate_comm_email_senders` check on the server
      // will 422 with `err_*_sender_mailbox_not_connected`. Trap that
      // client-side before shipping the whole payload so the user
      // doesn't lose what they typed.
      const comm = (payload.communication ?? {}) as {
        send_email_to_candidate?: boolean;
        send_email_to_interviewer?: boolean;
      };
      const wantsToSendEmail =
        comm.send_email_to_candidate === true ||
        comm.send_email_to_interviewer === true;
      if (wantsToSendEmail && !isMailBoxConnected) {
        toast({
          title: 'Email not connected',
          status: 'error',
        });
        return;
      }
      // /ats_interviews wants multipart so
      // `candidate_email_comm_config.attachments[]` can carry raw
      // `File` blobs (CORE-4040 backend). /ats_schedules does its own
      // `convertToFormData` inside `useSelfSchedule`, so we hand it
      // the plain object to avoid double-serialisation.
      const interviewFormData = buildSchedulePayloadFormData(
        payload,
        contextIds,
        mode
      );
      const selfScheduleJson = buildSchedulePayload(payload, contextIds, mode);

      try {
        // Phone edit → contact update. Only fires when the user
        // actually opened the pencil AND the new value differs from
        // what came in via `initialCandidatePhone`. A read-only
        // glance at the existing number skips this. `candidate_phone`
        // itself stays stripped from the interview payload via
        // `UI_ONLY_FIELDS` — `/ats_interviews` doesn't accept it.
        const editedPhone = payload.details.candidate_phone as
          | PhoneValue
          | null
          | undefined;
        const phoneWasEdited =
          payload.details.isCandidatePhoneEditing === 'edit' &&
          !phoneValuesEqual(editedPhone, initialCandidatePhone);
        if (
          phoneWasEdited &&
          typeof candidateId === 'number' &&
          candidateId > 0 &&
          editedPhone?.number
        ) {
          await api.put(
            `/contacts/${candidateId}`,
            {
              phone_code: editedPhone.dialCode ?? '',
              phone_number: editedPhone.number ?? '',
            },
            { client: 'api' }
          );
        }

        if (isSelfSchedule) {
          if (isEdit && scheduleId) {
            await updateSelfSchedule.mutateAsync({
              scheduleId,
              payload: selfScheduleJson,
            });
          } else {
            await createSelfSchedule.mutateAsync({
              payload: selfScheduleJson,
            });
          }
        } else {
          if (isEdit && interviewId) {
            await updateInterview.mutateAsync({
              interviewId,
              payload: interviewFormData,
            });
          } else {
            await createInterview.mutateAsync({
              payload: interviewFormData,
            });
          }
        }

        // Self-schedule edit opens from `SelfShedule.tsx` (legacy
        // wrapper) which fires its own "Interview updated successfully"
        // toast in `onSuccess` — skip the wizard's own toast there so
        // the user doesn't see both. Every other path (manual create
        // + edit, self-schedule create) relies on the wizard toast.
        const outerHandlesToast = isSelfSchedule && isEdit;
        if (!outerHandlesToast) {
          toast({
            title: isEdit
              ? SCHEDULE_INTERVIEW_COPY.toast.updateSuccess.title
              : SCHEDULE_INTERVIEW_COPY.toast.createSuccess.title,
            status: 'success',
          });
        }
        onSuccess?.();
        onClose?.();
      } catch (err) {
        // Surface backend error codes via the shared `scheduler` error
        // map so users get actionable messages (e.g.
        // `err_interviewer_email_sender_mailbox_not_connected` → "The
        // selected 'From' user hasn't connected their mailbox…").
        // Falls back to the generic copy only when the map doesn't
        // know the code.
        const errors =
          err instanceof ApiError && err.errors.length > 0
            ? err.errors
            : undefined;
        const description = errors
          ? getErrorMessage({ errors }, 'scheduler')
          : SCHEDULE_INTERVIEW_COPY.toast.saveError.fallback;
        toast({
          title: SCHEDULE_INTERVIEW_COPY.toast.saveError.title,
          description,
          status: 'error',
        });
      }
    },
    [
      mode,
      applicationId,
      candidateId,
      initialCandidatePhone,
      isMailBoxConnected,
      jobId,
      stageId,
      stageActivityId,
      createInterview,
      createSelfSchedule,
      interviewId,
      isEdit,
      isSelfSchedule,
      onClose,
      onSuccess,
      scheduleId,
      toast,
      updateInterview,
      updateSelfSchedule,
    ]
  );

  // ---- Build TabItems from mode config ----
  const tabItems = useMemo(
    (): TabItem[] =>
      config.steps.map((step) => ({
        label: step.label,
        value: step.key,
      })),
    [config.steps]
  );

  // ---- Step validation orchestration ----
  const finalActionLabel = isEdit
    ? SCHEDULE_INTERVIEW_COPY.footer.saveInterview
    : SCHEDULE_INTERVIEW_COPY.footer.scheduleInterview;

  const stepper = useStepValidation<ScheduleInterviewPayload>({
    tabItems,
    payloadRef,
    stepRefs,
    onComplete: handleComplete,
    finalActionLabel,
    nextPrefix: 'Next:',
    backPrefix: 'Back:',
    enableBack: true,
    wizardCompleteMode: 'validateAllSteps',
    activateFirstInvalidStepOnWizardComplete: true,
  });

  // ---- "Schedule interview now" / "Update interview" secondary CTA ----
  // Same behaviour in both modes: validate every step in order, sync
  // each to the payload, and hand off to `handleComplete` to submit.
  // If a step's validate returns false, jump the user to that step so
  // the error is visible — don't advance, don't submit. "Schedule
  // interview now" is explicitly the shortcut that skips the step
  // navigation, so it must NOT call `goNextOrComplete` (which would
  // just walk the user to the next tab).
  const scheduleNow = useCallback(async () => {
    for (const step of config.steps) {
      const api = stepRefsMap.current[step.key]?.current;
      if (!api) continue;
      const ok = await api.validate();
      if (!ok) {
        stepper.onStepChange(step.key);
        return;
      }
      api.syncToPayload();
    }
    await handleComplete(payloadRef.current);
  }, [config.steps, handleComplete, stepper]);

  return {
    config,
    payloadRef,
    getStepRef,
    isSaving,
    scheduleNow,
    prefillVersion,
    interviewPlan,
    showEditLoader: isEdit && isLoadingExisting,
    showEditError: isEdit && isLoadError,
    /** Create flow: wait for `ats_interviews/new` to resolve before
     *  mounting the form. FormBuilder freezes `defaultValues` at mount,
     *  so without this gate the form shows empty and the plan-derived
     *  prefill silently no-ops after the data arrives. */
    showCreateLoader: !isEdit && planEnabled && isLoadingPlan,
    // From useStepValidation
    tabItems: stepper.tabItems,
    activeStep: stepper.activeStep,
    isBusy: stepper.isBusy,
    onStepChange: stepper.onStepChange,
    goNextOrComplete: stepper.goNextOrComplete,
    goBack: stepper.goBack,
    canGoBack: stepper.canGoBack,
    primaryButtonLabel: stepper.primaryButtonLabel,
    backButtonLabel: stepper.backButtonLabel,
    /** Last step key — callers use this to hide the "Update interview"
     *  secondary CTA on the final step (the primary CTA already does
     *  the save there, so showing both would be a visual duplicate). */
    lastStepKey: config.steps[config.steps.length - 1]?.key,
  };
}
